/**
 * CR154: Enhanced fold line settings with per-size configurable heights
 * 
 * Replaces the simple useFoldLine hook with a structured settings approach:
 * - Per-size heights: compact, normal, large
 * - DB-backed persistence via app_config table
 * - localStorage fallback for offline/fast load
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

// ================================================================
// TYPES
// ================================================================

export interface FoldLineHeights {
  compact: number;
  normal: number;
  large: number;
}

export interface FoldLineSettings {
  enabled: boolean;
  heights: FoldLineHeights;
  /** CR154.2: Show compare view with all sizes */
  compareMode: boolean;
}

export type CardSizeKey = "compact" | "normal" | "large";

// ================================================================
// DEFAULTS
// ================================================================

export const DEFAULT_FOLD_LINE_HEIGHTS: FoldLineHeights = {
  compact: 200,
  normal: 400,
  large: 600,
};

export const DEFAULT_FOLD_LINE_SETTINGS: FoldLineSettings = {
  enabled: true,
  heights: { ...DEFAULT_FOLD_LINE_HEIGHTS },
  compareMode: false,
};

const STORAGE_KEY = "ui.foldLineSettings";
const DB_CONFIG_KEY = "fold_line_settings";

// ================================================================
// HOOK
// ================================================================

export function useFoldLineSettings() {
  const [settings, setSettingsState] = useState<FoldLineSettings>(DEFAULT_FOLD_LINE_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load from localStorage first (fast), then DB (source of truth)
  useEffect(() => {
    let mounted = true;

    // Step 1: Load from localStorage for instant UI
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (mounted && parsed && typeof parsed === "object") {
          setSettingsState({
            enabled: parsed.enabled ?? DEFAULT_FOLD_LINE_SETTINGS.enabled,
            heights: {
              compact: parsed.heights?.compact ?? DEFAULT_FOLD_LINE_HEIGHTS.compact,
              normal: parsed.heights?.normal ?? DEFAULT_FOLD_LINE_HEIGHTS.normal,
              large: parsed.heights?.large ?? DEFAULT_FOLD_LINE_HEIGHTS.large,
            },
            compareMode: parsed.compareMode ?? false,
          });
        }
      }
    } catch {
      // localStorage not available or parse error
    }

    // Step 2: Load from DB (async)
    const loadFromDb = async () => {
      try {
        const { data, error } = await supabase
          .from("app_config")
          .select("value_json")
          .eq("key", DB_CONFIG_KEY)
          .maybeSingle();

        if (error) {
          console.error("[useFoldLineSettings] DB load error:", error);
        } else if (data?.value_json && mounted) {
          const dbSettings = data.value_json as Record<string, unknown>;
          const heights = dbSettings.heights as Record<string, number> | undefined;
          
          const loadedSettings: FoldLineSettings = {
            enabled: typeof dbSettings.enabled === "boolean" ? dbSettings.enabled : DEFAULT_FOLD_LINE_SETTINGS.enabled,
            heights: {
              compact: heights?.compact ?? DEFAULT_FOLD_LINE_HEIGHTS.compact,
              normal: heights?.normal ?? DEFAULT_FOLD_LINE_HEIGHTS.normal,
              large: heights?.large ?? DEFAULT_FOLD_LINE_HEIGHTS.large,
            },
            compareMode: typeof dbSettings.compareMode === "boolean" ? dbSettings.compareMode : false,
          };
          
          setSettingsState(loadedSettings);
          
          // Sync to localStorage
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(loadedSettings));
          } catch {
            // ignore
          }
        }
      } catch (err) {
        console.error("[useFoldLineSettings] Unexpected error:", err);
      } finally {
        if (mounted) setIsLoaded(true);
      }
    };

    loadFromDb();

    return () => {
      mounted = false;
    };
  }, []);

  // Save to both localStorage and DB
  const saveSettings = useCallback(async (newSettings: FoldLineSettings): Promise<boolean> => {
    setIsSaving(true);
    
    // Validate heights
    const validatedSettings: FoldLineSettings = {
      enabled: newSettings.enabled,
      heights: {
        compact: clampHeight(newSettings.heights.compact),
        normal: clampHeight(newSettings.heights.normal),
        large: clampHeight(newSettings.heights.large),
      },
      compareMode: newSettings.compareMode,
    };

    try {
      // Save to localStorage first (fast)
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(validatedSettings));
      } catch {
        // ignore
      }

      // Update local state
      setSettingsState(validatedSettings);

      // Save to DB - cast to Json type for Supabase
      const jsonPayload = {
        enabled: validatedSettings.enabled,
        heights: {
          compact: validatedSettings.heights.compact,
          normal: validatedSettings.heights.normal,
          large: validatedSettings.heights.large,
        },
        compareMode: validatedSettings.compareMode,
      };

      const { error } = await supabase
        .from("app_config")
        .upsert([{
          key: DB_CONFIG_KEY,
          value_json: jsonPayload,
          updated_at: new Date().toISOString(),
        }], {
          onConflict: "key",
        });

      if (error) {
        console.error("[useFoldLineSettings] DB save error:", error);
        return false;
      }

      return true;
    } catch (err) {
      console.error("[useFoldLineSettings] Save error:", err);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Quick setters for individual properties
  const setEnabled = useCallback((enabled: boolean) => {
    setSettingsState(prev => {
      const newSettings = { ...prev, enabled };
      // Async save (fire and forget for quick toggle)
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  const setCompareMode = useCallback((compareMode: boolean) => {
    setSettingsState(prev => {
      const newSettings = { ...prev, compareMode };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  const setHeight = useCallback((size: CardSizeKey, height: number) => {
    setSettingsState(prev => ({
      ...prev,
      heights: {
        ...prev.heights,
        [size]: clampHeight(height),
      },
    }));
  }, []);

  const resetDefaults = useCallback(async (): Promise<boolean> => {
    return saveSettings(DEFAULT_FOLD_LINE_SETTINGS);
  }, [saveSettings]);

  // Get height for a specific card size
  const getHeightForSize = useCallback((size: CardSizeKey): number => {
    return settings.heights[size];
  }, [settings.heights]);

  // Memoized for stability
  const stableSettings = useMemo(() => settings, [
    settings.enabled,
    settings.compareMode,
    settings.heights.compact,
    settings.heights.normal,
    settings.heights.large,
  ]);

  return {
    settings: stableSettings,
    isLoaded,
    isSaving,
    setEnabled,
    setCompareMode,
    setHeight,
    saveSettings,
    resetDefaults,
    getHeightForSize,
  };
}

// ================================================================
// UTILITIES
// ================================================================

function clampHeight(value: number): number {
  const min = 0;
  const max = 2000;
  const step = 10;
  
  if (isNaN(value) || !isFinite(value)) {
    return DEFAULT_FOLD_LINE_HEIGHTS.normal;
  }
  
  // Clamp to range
  const clamped = Math.max(min, Math.min(max, value));
  
  // Round to nearest step
  return Math.round(clamped / step) * step;
}

/**
 * Map DealCardUX cardSize to fold line size key
 */
export function mapCardSizeToFoldKey(cardSize: "compact" | "regular" | "large"): CardSizeKey {
  switch (cardSize) {
    case "compact":
      return "compact";
    case "regular":
      return "normal";
    case "large":
      return "large";
    default:
      return "normal";
  }
}

export default useFoldLineSettings;
